import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { useToast } from "@/contexts/ToastContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SignatureDish {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
}

export interface HomePageContent {
  hero: { backgroundImage: string };
  welcome: {
    leftImage: string;
    rightTopImage: string;
    rightBottomImage: string;
  };
  dining: { image: string };
}

export interface DiningPageContent {
  heroImage: string;
  philosophyImage: string;
  breakfastImage: string;
  lunchImage: string;
  dinnerImage: string;
  dishes: SignatureDish[];
}

export interface AboutPageContent {
  heroImage: string;
  mainImage: string;
  gridImage1: string;
  gridImage2: string;
}

export type PageId = "homepage" | "dining-page" | "about-page";

// ─── Empty defaults ───────────────────────────────────────────────────────────

export const HOME_DEFAULTS: HomePageContent = {
  hero: { backgroundImage: "" },
  welcome: { leftImage: "", rightTopImage: "", rightBottomImage: "" },
  dining: { image: "" },
};

export const DINING_DEFAULTS: DiningPageContent = {
  heroImage: "",
  philosophyImage: "",
  breakfastImage: "",
  lunchImage: "",
  dinnerImage: "",
  dishes: [],
};

export const ABOUT_DEFAULTS: AboutPageContent = {
  heroImage: "",
  mainImage: "",
  gridImage1: "",
  gridImage2: "",
};

// ─── Generic fetch / save ─────────────────────────────────────────────────────

async function getPageContent<T>(pageId: PageId, defaults: T): Promise<T> {
  const snap = await getDoc(doc(db, "content", pageId));
  if (!snap.exists()) return defaults;
  return { ...defaults, ...snap.data() } as T;
}

async function savePageContent<T extends object>(
  pageId: PageId,
  data: T,
): Promise<void> {
  await setDoc(
    doc(db, "content", pageId),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

// ─── Homepage ─────────────────────────────────────────────────────────────────

export function useHomePageContent() {
  return useQuery({
    queryKey: ["pageContent", "homepage"],
    queryFn: () => getPageContent<HomePageContent>("homepage", HOME_DEFAULTS),
    staleTime: 0,
  });
}

export function useUpdateHomePageContent() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: (data: HomePageContent) => savePageContent("homepage", data),
    onSuccess: (_, variables) => {
      qc.setQueryData(["pageContent", "homepage"], variables);
      success("Homepage content saved.");
    },
    onError: () => error("Failed to save. Please try again."),
  });
}

// ─── Dining page ──────────────────────────────────────────────────────────────

export function useDiningPageContent() {
  return useQuery({
    queryKey: ["pageContent", "dining-page"],
    queryFn: () =>
      getPageContent<DiningPageContent>("dining-page", DINING_DEFAULTS),
    staleTime: 0,
  });
}

export function useUpdateDiningPageContent() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: (data: DiningPageContent) =>
      savePageContent("dining-page", data),
    onSuccess: (_, variables) => {
      qc.setQueryData(["pageContent", "dining-page"], variables);
      success("Dining page content saved.");
    },
    onError: () => error("Failed to save. Please try again."),
  });
}

// ─── About page ───────────────────────────────────────────────────────────────

export function useAboutPageContent() {
  return useQuery({
    queryKey: ["pageContent", "about-page"],
    queryFn: () =>
      getPageContent<AboutPageContent>("about-page", ABOUT_DEFAULTS),
    staleTime: 0,
  });
}

export function useUpdateAboutPageContent() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: (data: AboutPageContent) => savePageContent("about-page", data),
    onSuccess: (_, variables) => {
      qc.setQueryData(["pageContent", "about-page"], variables);
      success("About page content saved.");
    },
    onError: () => error("Failed to save. Please try again."),
  });
}